import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  Form,
  Input,
  Switch,
  Tabs,
  Drawer,
  Typography,
  Collapse,
  Col,
  Row,
  Button,
  Space,
  Divider,
} from 'antd';
import { TextEditor } from '@yuhuinc/ui-library';
import { InfoCircleOutlined } from '@ant-design/icons';
import MultiInstanceFormFields from './MultiInstanceFormFields';
import DisplayOnlyBlockFields from './DisplayOnlyBlockFields';
import FormItemFactory from '../components/FormItemFactory';

import { getTranslatedString } from 'helpers/i18n';

const { Paragraph } = Typography;

const StyledSpace = styled(Space)`
  width: 100%;
  position: relative;
`;

const { Title } = Typography;
const { Panel } = Collapse;

const StyledRow = styled(Row)`
  margin-bottom: 16px;
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label {
    padding: 0;
  }
`;

const CustomTextEditorInput = ({ value, onChange, editorId }) => (
  <TextEditor defaultValue={value} editorId={editorId} onChange={onChange} />
);

const locales = {
  en: { key: 'en', label: getTranslatedString('languages.en') },
  fr: { key: 'fr', label: getTranslatedString('languages.fr') },
};

const BlockConfigurationDrawer = ({ stepBlockInitialData, isOpen, onClose, onUpdate }) => {
  const [form] = Form.useForm();
  const [selectedLocale, setSelectedLocale] = useState(locales.en.key);

  const title = () => stepBlockInitialData.translations[selectedLocale]?.title;
  const subtitle = () => stepBlockInitialData.translations[selectedLocale]?.description;

  const onSubmit = async () => {
    const values = await form.validateFields();
    onUpdate(values);
  };

  const stepBlockFields = () => {
    const blockFields = stepBlockInitialData.fields || [];
    const originalBlockFields = stepBlockInitialData.fields || [];

    return blockFields.length === 0 ? originalBlockFields : blockFields;
  };

  useEffect(() => form.resetFields(), [stepBlockInitialData]);

  const formInitialValues = {
    image_alt: stepBlockInitialData.image_alt || '',
    image_caption: stepBlockInitialData.image_caption || '',
    image_href: stepBlockInitialData.image_href || '',
    file_upload_id: stepBlockInitialData.file_upload_id || null,
    translations: stepBlockInitialData?.translations || {},
    fields: stepBlockFields(),
    has_rendering_question: stepBlockInitialData?.has_rendering_question || false,
  };

  const showMultiinstance = ['multi_instance_form', 'custom_parking'].includes(
    stepBlockInitialData.form_type
  );

  const showFields =
    formInitialValues.fields.length > 0 || stepBlockInitialData.form_type === 'display_only';

  if (showMultiinstance) {
    formInitialValues['min_instances'] = stepBlockInitialData.min_instances ?? 0;
    formInitialValues['max_instances'] = stepBlockInitialData.max_instances ?? 100;
  }

  const BlockForm = ({ locale }) => (
    <StyledSpace direction="vertical" size="middle">
      <Form.Item name={['translations', locale, 'locale']} initialValue={locale} hidden>
        <Input />
      </Form.Item>

      <Form.Item
        name={['translations', locale, 'title']}
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.prototypesDrawer.form.title'
        )}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name={['translations', locale, 'description']}
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.prototypesDrawer.form.description'
        )}
        requiredMark={'optional'}
        tooltip={{ title: '', icon: <InfoCircleOutlined /> }}
      >
        <CustomTextEditorInput editorId={'block-description'} />
      </Form.Item>

      <Form.Item
        name="has_rendering_question"
        label={getTranslatedString(
          'landlordPortal.applicationTemplate.steps.form.hasRenderingQuestion'
        )}
        valuePropName="checked"
      >
        <Switch size={'small'} />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.has_rendering_question !== currentValues.has_rendering_question
        }
      >
        {({ getFieldValue }) =>
          getFieldValue('has_rendering_question') ? (
            <Form.Item
              name={['translations', locale, 'rendering_question']}
              label={getTranslatedString(
                'landlordPortal.applicationTemplate.steps.form.renderingQuestion'
              )}
              hasFeedback
              rules={[{ required: true, message: getTranslatedString('requiredField') }]}
            >
              <Input />
            </Form.Item>
          ) : null
        }
      </Form.Item>

      {showMultiinstance && (
        <>
          <Divider />
          <MultiInstanceFormFields />
        </>
      )}

      <Divider />
    </StyledSpace>
  );

  const BlockFieldsForm = ({ locale }) => (
    <>
      {stepBlockInitialData.form_type === 'display_only' ? (
        <DisplayOnlyBlockFields form={form} originalImageUrl={stepBlockInitialData.image_url} />
      ) : (
        <>
          <Title level={5}>
            {getTranslatedString(
              'landlordPortal.applicationTemplate.prototypesDrawer.availableData'
            )}
          </Title>

          <Form.List name="fields">
            {(fields) => (
              <>
                {fields.map(({ name, key }) => (
                  <StyledRow key={key}>
                    <Col span={18}>
                      <Collapse bordered={false}>
                        <Panel
                          header={form.getFieldValue([
                            'fields',
                            name,
                            'translations',
                            locale,
                            'label',
                          ])}
                        >
                          <Form.Item
                            name={[name, 'translations', locale, 'locale']}
                            initialValue={locale}
                            hidden
                          >
                            <Input />
                          </Form.Item>

                          <FormItemFactory
                            name={name}
                            locale={locale}
                            label={form.getFieldValue([
                              'fields',
                              name,
                              'translations',
                              locale,
                              'label',
                            ])}
                            placeholder={form.getFieldValue([
                              'fields',
                              name,
                              'translations',
                              locale,
                              'placeholder',
                            ])}
                            prototypeOf={form.getFieldValue(['fields', name, 'field_type'])}
                            flag={form.getFieldValue(['fields', name, 'flag'])}
                            availableOptions={form.getFieldValue([
                              'fields',
                              name,
                              'translations',
                              locale,
                              'available_options',
                            ])}
                            allOptions={form.getFieldValue([
                              'fields',
                              name,
                              'translations',
                              locale,
                              'all_options',
                            ])}
                          />
                        </Panel>
                      </Collapse>
                    </Col>

                    <Col span={6}>
                      <Space style={{ marginLeft: '8px' }}>
                        <StyledFormItem
                          name={[name, 'active']}
                          label={getTranslatedString('landlordPortal.active')}
                          valuePropName="checked"
                        >
                          <Switch
                            size="small"
                            disabled={form.getFieldValue(['fields', name, 'prototype_required'])}
                          />
                        </StyledFormItem>

                        <StyledFormItem
                          name={[name, 'required']}
                          label="Req’d"
                          valuePropName="checked"
                        >
                          <Switch
                            size="small"
                            disabled={form.getFieldValue(['fields', name, 'prototype_required'])}
                          />
                        </StyledFormItem>

                        <StyledFormItem
                          name={[name, 'flag']}
                          label={getTranslatedString('landlordPortal.flag')}
                          valuePropName="checked"
                        >
                          <Switch
                            size="small"
                            disabled={!form.getFieldValue(['fields', name, 'flaggable'])}
                          />
                        </StyledFormItem>
                      </Space>
                    </Col>
                  </StyledRow>
                ))}
              </>
            )}
          </Form.List>
        </>
      )}
    </>
  );

  return (
    <Drawer
      title={title}
      closable
      visible={isOpen}
      onClose={onClose}
      width={598}
      extra={
        <Space>
          <Button onClick={onClose}>{getTranslatedString('cancel')}</Button>
          <Button onClick={onSubmit} type="primary">
            {getTranslatedString('save')}
          </Button>
        </Space>
      }
    >
      {subtitle && <Paragraph>{subtitle}</Paragraph>}

      <Form layout="vertical" form={form} initialValues={formInitialValues}>
        <Tabs defaultActiveKey={selectedLocale} onChange={setSelectedLocale}>
          <Tabs.TabPane tab={locales.en.label} key={locales.en.key}>
            <BlockForm locale={locales.en.key} />
            {showFields && <BlockFieldsForm locale={locales.en.key} />}
          </Tabs.TabPane>

          <Tabs.TabPane tab={locales.fr.label} key={locales.fr.key}>
            <BlockForm locale={locales.fr.key} />
            {showFields && <BlockFieldsForm locale={locales.fr.key} />}
          </Tabs.TabPane>
        </Tabs>
      </Form>
    </Drawer>
  );
};

export default BlockConfigurationDrawer;
